import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Home, Users, FileText, LayoutPanelLeft, LogOut, X, Image, BarChart3, CheckSquare, Package, Calendar, Shield, Settings } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  key: string;
  path: string;
  label: string;
  icon: typeof Home;
  badgeKey?: 'newLeads' | 'todayTodos';
}

const NAV_I18N_KEYS: Record<string, string> = {
  dashboard: 'nav.dashboard',
  todos: 'nav.todos',
  calendar: 'nav.calendar',
  leads: 'nav.leads',
  products: 'nav.products',
  forms: 'nav.forms',
  'site-pages': 'nav.myPages',
  analytics: 'nav.analytics',
  media: 'nav.media',
};

const ALL_NAV_ITEMS: NavItem[] = [
  { key: 'dashboard', path: '/dashboard', label: 'Dashboard', icon: Home },
  { key: 'todos', path: '/todos', label: 'To-Dos', icon: CheckSquare, badgeKey: 'todayTodos' },
  { key: 'calendar', path: '/calendar', label: 'Kalender', icon: Calendar },
  { key: 'leads', path: '/leads', label: 'Leads', icon: Users, badgeKey: 'newLeads' },
  { key: 'products', path: '/products', label: 'Produkte', icon: Package },
  { key: 'forms', path: '/forms', label: 'Formulare', icon: FileText },
  { key: 'site-pages', path: '/site/pages', label: 'Meine Seiten', icon: LayoutPanelLeft },
  { key: 'analytics', path: '/analytics', label: 'Analytics', icon: BarChart3 },
  { key: 'media', path: '/media', label: 'Medien', icon: Image },
];

export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { signOut, profile, isAdmin, user } = useAuth();
  const { menuOrder, hiddenMenuItems } = useSettings();
  const { t } = useTranslation();
  const location = useLocation();
  const [badges, setBadges] = useState<{ newLeads: number; todayTodos: number }>({
    newLeads: 0,
    todayTodos: 0,
  });

  useEffect(() => {
    if (!user?.id) return;

    const fetchCounts = async () => {
      try {
        const { count: leadsCount } = await supabase
          .from('leads')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('status', 'NEW');

        const today = new Date().toISOString().split('T')[0];
        const { count: todosCount } = await supabase
          .from('todos')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', user.id)
          .eq('completed', false)
          .lte('due_date', today);

        setBadges({ newLeads: leadsCount || 0, todayTodos: todosCount || 0 });
      } catch (error) {
        logger.error('Error fetching sidebar counts:', error);
      }
    };

    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, [user?.id]);

  const navItems = useMemo(() => {
    const visibleItems = ALL_NAV_ITEMS.filter(
      (item) => !hiddenMenuItems.includes(item.key)
    );
    return visibleItems.sort((a, b) => {
      const indexA = menuOrder.indexOf(a.key);
      const indexB = menuOrder.indexOf(b.key);
      const posA = indexA === -1 ? 999 : indexA;
      const posB = indexB === -1 ? 999 : indexB;
      return posA - posB;
    });
  }, [menuOrder, hiddenMenuItems]);

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const handleSignOut = async () => { await signOut(); };
  const handleNavClick = () => { onClose(); };

  const firstName = profile?.full_name?.split(' ')[0] || 'User';
  const initials = profile?.full_name
    ? profile.full_name.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase()
    : 'U';

  return (
    <>
      <aside
        data-tour="sidebar"
        className={`
          fixed top-0 left-0 h-full z-50 flex flex-col
          transition-transform duration-300 lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          w-64
        `}
        style={{
          background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, rgba(255,255,255,0.20) 100%)',
          backdropFilter: 'blur(14px) saturate(140%)',
          WebkitBackdropFilter: 'blur(14px) saturate(140%)',
          borderRight: '1px solid var(--bt-line-soft)',
        }}
      >
        {/* Logo */}
        <div className="flex items-center justify-between px-4 pt-5 pb-4">
          <Link to="/dashboard" className="flex items-center gap-2.5" onClick={handleNavClick}>
            <div
              style={{
                width: 30,
                height: 30,
                background: 'var(--bt-ink)',
                borderRadius: 9,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <span
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontStyle: 'italic',
                  color: 'var(--bt-ink-2)',
                  fontSize: 16,
                  fontWeight: 500,
                  lineHeight: 1,
                }}
              >
                m
              </span>
            </div>
            <span
              style={{
                fontFamily: "'Fraunces', serif",
                fontStyle: 'italic',
                fontSize: 20,
                fontWeight: 500,
                color: 'var(--bt-ink)',
                letterSpacing: '-0.02em',
              }}
            >
              mamal
            </span>
          </Link>
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-lg transition-colors"
            style={{ color: 'var(--bt-mute)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3.5 space-y-0.5 overflow-y-auto">
          {isAdmin && (
            <Link
              to="/admin"
              onClick={handleNavClick}
              className={`flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] transition-all mb-1 ${
                isActive('/admin') ? 'bt-nav-active' : ''
              }`}
              style={
                isActive('/admin')
                  ? {}
                  : {
                      color: 'var(--bt-mute)',
                    }
              }
              onMouseEnter={(e) => {
                if (!isActive('/admin')) e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
              }}
              onMouseLeave={(e) => {
                if (!isActive('/admin')) e.currentTarget.style.background = 'transparent';
              }}
            >
              <Shield className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--bt-accent)' }} />
              <span className="text-sm font-medium flex-1" style={{ color: 'var(--bt-accent)' }}>
                {t('nav.admin')}
              </span>
            </Link>
          )}

          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={handleNavClick}
                className={`flex items-center gap-2.5 px-3 py-2.5 rounded-[10px] transition-all ${
                  active ? 'bt-nav-active' : ''
                }`}
                style={active ? {} : { color: 'var(--bt-mute)' }}
                onMouseEnter={(e) => {
                  if (!active) e.currentTarget.style.background = 'rgba(255,255,255,0.3)';
                }}
                onMouseLeave={(e) => {
                  if (!active) e.currentTarget.style.background = 'transparent';
                }}
              >
                <Icon
                  className="w-4 h-4 flex-shrink-0"
                  style={{ color: active ? 'var(--bt-ink)' : 'var(--bt-mute)' }}
                />
                <span
                  className="text-sm font-medium flex-1"
                  style={{ color: active ? 'var(--bt-ink)' : 'var(--bt-mute)' }}
                >
                  {t(NAV_I18N_KEYS[item.key] || item.label)}
                </span>
                {badgeCount > 0 && (
                  <span className="bt-badge">
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="px-3.5 py-4" style={{ borderTop: '1px solid var(--bt-line-soft)' }}>
          <Link
            to="/profile"
            onClick={handleNavClick}
            className="flex items-center gap-2.5 px-3 py-2 rounded-[10px] transition-all mb-1"
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            {profile?.profile_image_url ? (
              <img
                src={profile.profile_image_url}
                alt="Profil"
                className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                style={{ border: '2px solid var(--bt-glass-stroke)' }}
              />
            ) : (
              <div
                className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold"
                style={{
                  background: 'var(--bt-ink)',
                  color: 'var(--bt-ink-2)',
                  border: '2px solid var(--bt-glass-stroke)',
                }}
              >
                {initials}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: 'var(--bt-ink)' }}>
                {profile?.full_name || firstName}
              </p>
              <p className="text-xs truncate" style={{ color: 'var(--bt-mute)' }}>
                {t('nav.profile')}
              </p>
            </div>
            <Link
              to="/einstellungen"
              onClick={(e) => { e.stopPropagation(); handleNavClick(); }}
              className="p-1.5 rounded-lg transition-colors flex-shrink-0"
              style={{ color: 'var(--bt-mute)' }}
              title="Einstellungen"
            >
              <Settings className="w-4 h-4" />
            </Link>
          </Link>

          <button
            onClick={handleSignOut}
            className="flex items-center gap-2.5 px-3 py-2 rounded-[10px] w-full transition-all"
            style={{ color: 'var(--bt-mute)' }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            <LogOut className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm font-medium">{t('nav.logout')}</span>
          </button>
        </div>
      </aside>

      {isOpen && (
        <div
          className="fixed inset-0 z-40 lg:hidden !mt-0"
          style={{ background: 'rgba(26,23,38,0.3)', backdropFilter: 'blur(4px)' }}
          onClick={onClose}
        />
      )}
    </>
  );
}
